import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import {
  editPenIcon,
  closePopupIcon,
  seeTransactionsIcon,
  emailIcon,
  dateIcon,
  timeIcon,
  changePasswordIcon,
  generalSettingIcon,
} from '../assets/superAdminAssets';
import SideNavigation from '../components/SideNavigation';
import Header from '../components/Header';
import CompanyDrawer from '../components/CompanyDrawer';
import DataTable from '../components/DataTable';
import { useAppContext } from '../contexts/AppContext';
import { listSubscriptionPlans } from '../services/subscriptionPlan.service';
import { Constants } from '../constants';
import { extendTrialPeriod, getAllCompaniesList, resendActivationEmail, updateCompanyStatus } from '../services/company.service';
import moment from 'moment';
import { formatDollars } from '../helpers/number.helper';
import { getBoolean } from '../helpers/index.helper';
import DatePicker from 'react-datepicker';
import CompanyPayments from '../components/CompanyPayments';
import CompanyAdminHistory from '../components/CompanyAdminHistory';
import ChangePasswordModal from '../components/ChangePasswordModal';
import SMTPSettings from '../components/SMTPSettings';

const PLAN_PAGE_SIZE = 20;

const Companies = () => {
  const { showLoader, hideLoader, showToaster, hideToaster, isLoading } = useAppContext();
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showTransactionsModal, setShowTransactionsModal] = useState(false);
  const [showTransactionsModalAnimation, setShowTransactionsModalAnimation] = useState(false);
  const [showAdminHistoryModalAnimation, setShowAdminHistoryModalAnimation] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false);
  const [showAdminHistoryModal, setShowAdminHistoryModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSmtpSettingsModal, setShowSmtpSettingsModal] = useState(false);

  const [subscriptionPlanList, setSubscriptionPlanList] = useState([]);
  const [planPage, setPlanPage] = useState(1);
  const [planSearchText, setPlanSearchText] = useState('');
  const [hasMorePlans, setHasMorePlans] = useState(true);
  const [plansLoadingMore, setPlansLoadingMore] = useState(false);
  const [planFilter, setPlanFilter] = useState(null);
  const [listCustomPlans, setListCustomPlans] = useState(false);
  const planSearchDebounceRef = useRef(null);

  const [companiesData, setCompaniesData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  const [selectedCompanyId, setSelectedCompanyId] = useState('0');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [newTrialEndDate, setNewTrialEndDate] = useState(new Date());
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);

  const [showStatusConfirmPopup, setShowStatusConfirmPopup] = useState(false);
  const [statusConfirmCompany, setStatusConfirmCompany] = useState(null);

  useEffect(() => {
    document.title = 'Loan Mantra - Companies';
    showLoader();
  }, []);

  const fetchSubscriptionPlans = useCallback(async (pageIndex, search, append) => {
    try {
      setPlansLoadingMore(true);
      const res = await listSubscriptionPlans(pageIndex, PLAN_PAGE_SIZE, search || '');
      if (res?.status === 200) {
        let data = [];
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          data = Array.isArray(parsed) ? parsed : [];
        }
        if (append) {
          setSubscriptionPlanList((prev) => [...prev, ...data]);
        } else {
          setSubscriptionPlanList(data);
        }
        setPlanPage(pageIndex);
        setHasMorePlans(data.length >= PLAN_PAGE_SIZE);
      } else {
        if (!append) {
          showToaster(res.message, 'error');
          setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
        }
      }
    } catch (error) {
      console.log(error);
      if (!append) {
        showToaster('Failed to fetch subscription plans', 'error');
        setTimeout(() => hideToaster(), Constants.TOASTERHIDETIME);
      }
    } finally {
      setPlansLoadingMore(false);
    }
  }, [showLoader, hideLoader, showToaster, hideToaster]);

  useEffect(() => {
    fetchSubscriptionPlans(1, planSearchText, false);
  }, [planSearchText, fetchSubscriptionPlans]);

  const handlePlanInputChange = useCallback((value) => {
    if (planSearchDebounceRef.current) clearTimeout(planSearchDebounceRef.current);
    planSearchDebounceRef.current = setTimeout(() => {
      setPlanSearchText(value || '');
    }, 300);
  }, []);

  const handlePlanMenuScrollToBottom = useCallback(() => {
    if (plansLoadingMore || !hasMorePlans) return;
    fetchSubscriptionPlans(planPage + 1, planSearchText, true);
  }, [planPage, planSearchText, hasMorePlans, plansLoadingMore, fetchSubscriptionPlans]);

  // Handle transactions modal animation
  useEffect(() => {
    if (showTransactionsModal) {
      // Small delay to ensure the initial state is rendered before adding show class
      // This allows the browser to paint the initial state (translateX(100%)) first
      const timer = setTimeout(() => {
        setShowTransactionsModalAnimation(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // Remove show class immediately when closing to trigger exit animation
      setShowTransactionsModalAnimation(false);
    }
  }, [showTransactionsModal]);

  useEffect(() => {
    if (showAdminHistoryModal) {
      setShowAdminHistoryModalAnimation(true);
    } else {
      setShowAdminHistoryModalAnimation(false);
    }
  }, [showAdminHistoryModal]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchCompanies(page, rowsPerPage, searchText, planFilter?.PlanName);
    }, 500);
    return () => clearTimeout(timeout);
  }, [page, rowsPerPage, listCustomPlans, searchText, planFilter]);

  useEffect(() => {
    setPage(1);
  }, [searchText, planFilter]);

  async function fetchCompanies(pageIndex, pageSize, search, planFilter) {
    try {
      showLoader();
      const res = await getAllCompaniesList({
        PageIndex: pageIndex,
        PageSize: pageSize,
        SearchText: search || '',
        PlanFilter: planFilter || '',
        ...(listCustomPlans ? { listCustomPlans: true } : {})
      });
      if (res?.status === 200) {
        let data = [];
        if (res.resultObject) {
          const parsed = typeof res.resultObject === 'string' && res.resultObject !== '' ? JSON.parse(res.resultObject) : res.resultObject;
          data = parsed;
          setTotalCount(Number(res.message.split(" ")[1]));
        }
        setCompaniesData(data);
      } else {
        showToaster(res.message, 'error');
        setTimeout(() => {
          hideToaster();
        }, Constants.TOASTERHIDETIME);
      }
    } catch (error) {
      console.log(error);
      showToaster('Failed to fetch companies', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 0);
    }
  }

  const handleResendActivationEmail = useCallback(async (companyId) => {
    try {
      showLoader();
      const res = await resendActivationEmail(companyId);
      if (res?.status === 200) {
        showToaster(res.message, 'success');
        setShowResendEmailModal(false);
        setSelectedCompanyId('0');
        setSelectedCompanyName('');
      } else {
        showToaster(res.message, 'error');
      }
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } catch (error) {
      console.log(error);
      showToaster('Failed to resend activation email', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }, []);

  const handleExtendTrial = useCallback(async (companyId, newTrialEndDate) => {
    try {
      showLoader();
      const res = await extendTrialPeriod(companyId, moment(newTrialEndDate).format('YYYY-MM-DD'));
      if (res?.status === 200) {
        showToaster(res.message, 'success');
        setShowExtendTrialModal(false);
        setSelectedCompanyId('0');
        setSelectedCompanyName('');
        setNewTrialEndDate(new Date());
        fetchCompanies(page, rowsPerPage, searchText, planFilter?.PlanName);
      } else {
        showToaster(res.message, 'error');
      }
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } catch (error) {
      console.log(error);
      showToaster(error?.response?.data?.message || 'Failed to extend trial period', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }, []);


  const handleStatusConfirm = useCallback(async (id, isActive) => {
    try {
      showLoader();
      const res = await updateCompanyStatus({
        isActive: isActive,
        id: Number(id),
        updatedBy: 1,
        companyId: 0
      });
      if (res?.status === 200) {
        showToaster(res.message, 'success');
        setShowStatusConfirmPopup(false);
        setStatusConfirmCompany(null);
        fetchCompanies(page, rowsPerPage, searchText, planFilter?.PlanName);
      } else {
        showToaster(res.message, 'error');
      }
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } catch (error) {
      console.log(error);
      showToaster('Failed to update company status', 'error');
      setTimeout(() => {
        hideToaster();
      }, Constants.TOASTERHIDETIME);
    } finally {
      hideLoader();
    }
  }, [page, rowsPerPage, searchText, planFilter]);

  return (
    <>
      <SideNavigation />

      <Header
        title="Companies"
      />

      <div className="dashboard-wrap application-detail">
        <div className="p-3">
          <div className="d-flex justify-content-between pt-3">
            <div className="d-flex gap-15">
              <div className="lm-search-bar">
                <input
                  style={{ width: '300px' }}
                  type="text"
                  id="searchInput"
                  placeholder="Search by number, name, or other"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setPage(1); }}
                />
              </div>
              <div className="form-group" style={{ minWidth: 220 }}>
                <Select
                  classNamePrefix="select"
                  className="basic-single"
                  placeholder="All Plans"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  filterOption={() => true}
                  onInputChange={handlePlanInputChange}
                  onMenuScrollToBottom={handlePlanMenuScrollToBottom}
                  isLoading={plansLoadingMore}
                  options={[{ Id: 0, PlanName: 'Custom Plan' }, ...subscriptionPlanList]}
                  getOptionLabel={(opt) => opt.PlanName}
                  getOptionValue={(opt) => String(opt.Id)}
                  value={listCustomPlans ? { Id: 0, PlanName: 'Custom Plan' } : planFilter == null ? null : subscriptionPlanList.find((opt) => String(opt.Id) === String(planFilter.Id)) || null}
                  onChange={(option) => {
                    if (option?.Id === 0) {
                      setListCustomPlans(true);
                      setPlanFilter(null);
                    } else {
                      setListCustomPlans(false);
                      setPlanFilter(option || null)
                    }
                  }}
                  isClearable
                />
              </div>
            </div>

            <a onClick={() => setShowAddCompanyModal(true)} className="lm-button3">
              <span>+</span> Add a new Company
            </a>
          </div>
          <DataTable
            isLoading={isLoading}
            tableId="loanAppTable"
            columns={[
              {
                key: 'CompanyName',
                label: 'Company',
                sortable: true,
                render: (value, row) => {
                  const isTrialExpired = row.TrialEndsOn && moment.utc(row.TrialEndsOn).isBefore(moment.utc());
                  return <>
                    <a
                      className="application_no cursor-pointer"
                      onClick={() => { setShowAddCompanyModal(true); setSelectedCompanyId(row.CompanyId); setSelectedCompanyName(row.CompanyName ?? '-'); }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowAddCompanyModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}
                    >
                      {row.CompanyName ?? '-'}
                    </a>
                    <br />
                    {row?.TrialEndsOn
                      ? (
                        <>
                          <span className={`trial-tag ms-0 ${isTrialExpired ? 'expired' : ''}`}>
                            { row?.LastInvoiceDate ? 'Next billing date' : 'Trial ends on' } {moment.utc(row.TrialEndsOn).local().format('MMM D, YYYY')}
                          </span>
                          <br />
                        </>
                      )
                      : (
                        <>
                          <span className="trial-tag ms-0 expired">
                            { row?.LastInvoiceDate ? 'No billing date' : 'No trial period' }
                          </span>
                          <br />
                        </>
                      )}
                    {row.DomainName && (
                      <>
                        <Link to={`https://${row.DomainName}`} target="_blank" className="created_date lm-text-light">
                          {row.DomainName}
                        </Link>
                        <br />
                      </>
                    )}
                    {row.CompanyCreatedDate && (
                      <span className="created_date lm-text-light">
                        Created on {moment.utc(row.CompanyCreatedDate).local().format('MMM DD, YYYY')}
                      </span>
                    )}
                  </>
                },
              },
              {
                key: 'CompanyAdminFullName',
                label: 'Company Admin',
                sortable: true,
                render: (value, row) => {
                  const name = value ?? row.CompanyAdminFullName ?? "-";
                  return name;
                },
              },
              {
                key: 'CurrentPlan',
                label: 'Current Plan',
                sortable: true,
                render: (value, row) => {
                  const extraOtherUsers = Number(row.NumberOfOtherUsers) >= Number(row.PlanNoofUsers);
                  const extraBorrowers = Number(row.NumberOfBorrowers) >= Number(row.PlanNoofBorrowersLimit);
                  const extraPartners = Number(row.NumberOfPartners) >= Number(row.PlanNoofPartnersLimit);
                  const extraTransactions = Number(row.NumberOfTransactions) >= Number(row.PlanNoofTransactions);
                  return <>
                    {getBoolean(row?.IsCustomPlan) ? 'Custom Plan' : row?.CurrentPlan ? row?.CurrentPlan : '-'}
                    <>
                      <br />
                      {
                        row.UsersLimitedOrUnlimited?.toLowerCase() === 'unlimited' ? (
                          <span className="created_date color-green">Other Users: Unlimited</span>
                        ) : (
                          <span className={`created_date ${extraOtherUsers ? 'text-error' : 'lm-text-light'}`}>Other Users: {row.NumberOfOtherUsers ?? '-'} / {row.PlanNoofUsers ?? '-'}</span>
                        )
                      }
                      <br />
                      {
                        row.NoofBorrowersLimitedOrUnlimited?.toLowerCase() === 'unlimited' ? (
                          <span className="created_date color-green">Borrowers: Unlimited</span>
                        ) : (
                          <span className={`created_date ${extraBorrowers ? 'text-error' : 'lm-text-light'}`}>Borrowers: {row.NumberOfBorrowers ?? '-'} / {row.PlanNoofBorrowersLimit ?? '-'}</span>
                        )
                      }
                      <br />
                      {
                        row.NoofPartnersLimitedOrUnlimited?.toLowerCase() === 'unlimited' ? (
                          <span className="created_date color-green">Partners: Unlimited</span>
                        ) : (
                          <span className={`created_date ${extraPartners ? 'text-error' : 'lm-text-light'}`}>Partners: {row.NumberOfPartners ?? '-'} / {row.PlanNoofPartnersLimit ?? '-'}</span>
                        )
                      }
                      <br />
                      {
                        row.NoofTransactionsLimitedOrUnlimited?.toLowerCase() === 'unlimited' ? (
                          <span className="created_date color-green">Transactions: Unlimited</span>
                        ) : (
                          <span className={`created_date ${extraTransactions ? 'text-error' : 'lm-text-light'}`}>Transactions: {row.NumberOfTransactions ?? '-'} / {row.PlanNoofTransactions ?? '-'}</span>
                        )
                      }
                    </>
                  </>
                },
              },
              {
                key: 'LastInvoiceDate',
                label: 'Last Invoice',
                sortable: true,
                render: (value, row) => (
                  <>
                    {row?.LastInvoice ? `$${formatDollars(row?.LastInvoice)}` : '-'}
                    <br />
                    <span className="created_date lm-text-light">
                      {row.LastInvoiceDate ? moment.utc(row.LastInvoiceDate).local().format('MMM DD, YYYY') : '-'}
                    </span>
                  </>
                ),
              },
              {
                key: 'IsActive',
                label: 'Status',
                sortable: true,
                render: (value, row, rowIndex) => (
                  <div className="lm-toggle-switch">
                    <input
                      type="checkbox"
                      id={`switch-company-${row.CompanyId ?? row.Id ?? rowIndex}`}
                      checked={getBoolean(row.IsActive)}
                      onChange={() => {
                        setShowStatusConfirmPopup(true);
                        setStatusConfirmCompany({ id: row.CompanyId, companyName: row.CompanyName, isActive: !getBoolean(row.IsActive) });
                      }}
                      readOnly
                    />
                    <label htmlFor={`switch-company-${row.CompanyId ?? row.Id ?? rowIndex}`}>Status</label>
                  </div>
                ),
              },
              {
                key: 'action',
                label: 'Action',
                sortable: false,
                cellClassName: 'action-col',
                render: (_, row, rowIndex) => (
                  <div className="options_btn">
                    <span className="option_dots">...</span>
                    <div id={`popoverContent-${row.CompanyId ?? row.Id ?? rowIndex}`} className="app_options">
                      <div onClick={() => { setShowAddCompanyModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowAddCompanyModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                        <img className="me-2" src={editPenIcon} alt="" /> Edit
                      </div>
                      <div onClick={() => { setShowTransactionsModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowTransactionsModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                        <img className="me-2" src={seeTransactionsIcon} alt="" /> Payments
                      </div>
                      {
                        !getBoolean(row.IsActive) && (
                          <div onClick={() => { setShowResendEmailModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowResendEmailModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                            <img className="me-2" src={emailIcon} alt="" /> Resend activation email
                          </div>
                        )
                      }
                      {
                        !row?.LastInvoiceDate && (
                          <div onClick={() => { setShowExtendTrialModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); setNewTrialEndDate(row.TrialEndDate ?? new Date()); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowExtendTrialModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); setNewTrialEndDate(row.TrialEndDate ?? new Date()); } }}>
                            <img className="me-2" src={dateIcon} alt="" /> Extend trial
                          </div>
                        )
                      }
                      <div onClick={() => { setShowAdminHistoryModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowAdminHistoryModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                        <img className="me-2" src={timeIcon} alt="" /> Admin history
                      </div>
                      <div onClick={() => { setShowChangePasswordModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); setSelectedCompanyData(row); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowResendEmailModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                        <img className="me-2" src={changePasswordIcon} alt="" /> Change password
                      </div>
                      <div onClick={() => { setShowSmtpSettingsModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); setSelectedCompanyData(row); }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowSmtpSettingsModal(true); setSelectedCompanyId(row.CompanyId ?? ''); setSelectedCompanyName(row.CompanyName ?? '-'); } }}>
                        <img className="me-2" src={generalSettingIcon} alt="" /> Email Settings
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
            data={companiesData}
            defaultRowsPerPage={10}
            totalCount={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(val) => { setRowsPerPage(val); setPage(1); }}
          />
        </div>
      </div>

      {
        showAddCompanyModal && (
          <CompanyDrawer
            show={showAddCompanyModal}
            onClose={() => { setShowAddCompanyModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}
            onSubmit={() => {
              // Placeholder: handle create / edit company submit here if needed
              setShowAddCompanyModal(false);
              setSelectedCompanyId('0');
              fetchCompanies(page, rowsPerPage, searchText, planFilter?.PlanName);
            }}
            companyId={selectedCompanyId}
          />
        )
      }

      {showTransactionsModal && (
        <CompanyPayments
          show={showTransactionsModal}
          onClose={() => { setShowTransactionsModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}
          companyId={selectedCompanyId}
          showTransactionsModalAnimation={showTransactionsModalAnimation}
        />
      )}

      {showAdminHistoryModal && (
        <CompanyAdminHistory
          show={showAdminHistoryModal}
          onClose={() => { setShowAdminHistoryModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}
          companyId={selectedCompanyId}
          showAdminHistoryModalAnimation={showAdminHistoryModalAnimation}
          companyName={selectedCompanyName}
        />
      )}

      {showResendEmailModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowResendEmailModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}
            style={{ zIndex: 1040 }}
          ></div>
          <div
            className="modal show d-block fade"
            id="resendEmailModal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="resendEmailModalLabel"
            aria-hidden="false"
            style={{ zIndex: 1050 }}
            onClick={(e) => {
              // Close modal when clicking outside the modal-content
              if (e.target === e.currentTarget) {
                setShowResendEmailModal(false);
                setSelectedCompanyId('0');
                setSelectedCompanyName('');
              }
            }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="modal-title" id="resendEmailModalLabel">Resend Activation Email</h5>
                    <button type="button" className="bg-transparent border-none" data-dismiss="modal" aria-label="Close" onClick={() => setShowResendEmailModal(false)}>
                      <span aria-hidden="true"><img src={closePopupIcon} alt="" /></span>
                    </button>
                  </div>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to resend the activation email to the company admin?</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="lm-button2" data-dismiss="modal" onClick={() => { setShowResendEmailModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}>Cancel</button>
                  <button type="button" className="lm-button1" onClick={() => handleResendActivationEmail(selectedCompanyId)}>Confirm</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showExtendTrialModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowExtendTrialModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}
            style={{ zIndex: 1040 }}
          ></div>
          <div
            className="modal show d-block fade"
            id="extendTrialModal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="extendTrialModalLabel"
            aria-hidden="false"
            style={{ zIndex: 1050 }}
            onClick={(e) => {
              // Close modal when clicking outside the modal-content
              if (e.target === e.currentTarget) {
                setShowExtendTrialModal(false);
                setSelectedCompanyId('0');
                setSelectedCompanyName('');
              }
            }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="modal-title" id="extendTrialModalLabel">Extend trial period</h5>
                    <button type="button" className="bg-transparent border-none" data-dismiss="modal" aria-label="Close" onClick={() => { setShowExtendTrialModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); setNewTrialEndDate(new Date()); }}>
                      <span aria-hidden="true"><img src={closePopupIcon} alt="" /></span>
                    </button>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group input-date">
                        <label htmlFor="from_date">Select new trial end date</label>
                        <DatePicker
                          showIcon
                          toggleCalendarOnIconClick
                          isClearable={!!newTrialEndDate}
                          selected={newTrialEndDate}
                          dateFormat="MMMM d, yyyy"
                          placeholderText="Month DD, YYYY"
                          onChange={(date) => date ? setNewTrialEndDate(moment(date).format('YYYY-MM-DD')) : setNewTrialEndDate(null)}
                          yearDropdownItemNumber={100}
                          scrollableYearDropdown
                          showMonthDropdown
                          showYearDropdown
                          minDate={new Date()}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="lm-button2" data-dismiss="modal" onClick={() => { setShowExtendTrialModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); }}>Cancel</button>
                  <button type="button" className="lm-button1" onClick={() => handleExtendTrial(selectedCompanyId, newTrialEndDate)}>Confirm</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showStatusConfirmPopup && statusConfirmCompany && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => { setShowStatusConfirmPopup(false); setStatusConfirmCompany(null); }}
          ></div>
          <div className="modal show d-block fade" id="statusConfirmPopup" tabIndex="-1" role="dialog" aria-labelledby="statusConfirmPopupLabel" aria-hidden="false">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-body">
                  <h6 className="pt-4">
                    Are you sure you want to {statusConfirmCompany.isActive ? 'activate' : 'deactivate'} company &quot;{statusConfirmCompany.companyName}&quot;?
                  </h6>
                </div>
                <div className="modal-footer">
                  <button type="button" className="lm-button2" data-dismiss="modal" onClick={() => { setShowStatusConfirmPopup(false); setStatusConfirmCompany(null); }}>Cancel</button>
                  <button
                    type="button"
                    className="lm-button1"
                    onClick={() => {
                      handleStatusConfirm(statusConfirmCompany.id, statusConfirmCompany.isActive);
                      setShowStatusConfirmPopup(false);
                      setStatusConfirmCompany(null);
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showChangePasswordModal && (
        <ChangePasswordModal
          userdetails={selectedCompanyData}
          changePassPopupShow={showChangePasswordModal}
          setChangePassPopup={setShowChangePasswordModal}
          onClose={() => {
            setShowChangePasswordModal(false);
            setSelectedCompanyId('0');
            setSelectedCompanyName('');
            setSelectedCompanyData(null);
          }}
        />
      )}

      {
        showSmtpSettingsModal && (
          <SMTPSettings
            show={showSmtpSettingsModal}
            companyId={selectedCompanyId}
            showSMTPSettingsModalAnimation={showSmtpSettingsModal}
            companyName={selectedCompanyName}
            onClose={() => { setShowSmtpSettingsModal(false); setSelectedCompanyId('0'); setSelectedCompanyName(''); setSelectedCompanyData(null); }}
          />
        )
      }
    </>
  );
};

export default Companies;